import { logFirebaseEvent } from "../firebase";
import { canonicalTrackTitle } from "./trackTitleDecoration";
import {ANALYTICS_EVENT_BY_NAME, ANALYTICS_EVENT_NAMES, canonicalAnalyticsEventName} from "./analyticsEventRegistry";

const ALLOWED_EVENTS = new Set(ANALYTICS_EVENT_NAMES);

const PURCHASE_DEDUPE_KEY = "vtk_purchase_analytics_v1";
const MERCH_PURCHASE_DEDUPE_KEY = "vtk_merch_purchase_analytics_v1";

function safeText(value, max = 100) {
  return String(value ?? "").trim().slice(0, max);
}

function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

export function beatAnalyticsParams(track, extra = {}) {
  const params = {
    beat_id: safeText(track?.id, 150),
    beat_title: safeText(canonicalTrackTitle({...track, title: track?.title || track?.name}), 100),
    bpm: safeNumber(track?.bpm),
    musical_key: safeText(track?.key, 40),
    ...extra,
  };
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== "" && value !== undefined));
}

export function trackCommerceEvent(eventName, params = {}) {
  if (!ALLOWED_EVENTS.has(eventName)) return;
  const canonical = canonicalAnalyticsEventName(eventName);
  const definition = ANALYTICS_EVENT_BY_NAME[canonical];
  const allowed = new Set(definition?.parameters || []);
  const safe = Object.fromEntries(Object.entries(params).filter(([key]) => allowed.has(key)));
  logFirebaseEvent(canonical, safe);
}

export function canonicalCommerceType(item = {}) {
  const raw = [item.canonicalType, item.fulfillmentType, item.orderType, item.cartType,
    item.productType, item.type, item.kind, item.serviceType, item.category]
    .filter(Boolean).join(" ").toUpperCase();
  if (item.isCustomBeat || /CUSTOM[_\s-]?BEAT/.test(raw)) return "CUSTOM_BEAT";
  if (item.isMix || /MIX/.test(raw)) return "MIX";
  if (item.isRecording || /RECORDING|SESSION/.test(raw)) return "RECORDING";
  if (item.isPrintful || item.merch || /MERCH|PRINTFUL/.test(raw)) return "MERCH";
  if (item.exclusive || String(item.license || "").toLowerCase() === "exclusive" || /EXCLUSIVE/.test(raw)) return "EXCLUSIVE";
  return "LEASE";
}

export function commerceAnalyticsItems(items = []) {
  return (Array.isArray(items) ? items : []).map((item) => ({
    item_id: safeText(item?.id || item?.trackId || item?.productId, 100),
    item_name: safeText(item?.title || item?.name || canonicalCommerceType(item), 100),
    item_category: canonicalCommerceType(item),
    item_variant: safeText(item?.license || item?.mixType || "", 40),
    price: safeNumber(item?.price ?? (Number(item?.priceCents) / 100)),
    quantity: Math.max(1, Number(item?.quantity) || 1),
  })).map((item) => Object.fromEntries(Object.entries(item)
    .filter(([, value]) => value !== "" && value !== undefined)));
}

export function checkoutAnalyticsParams(items = []) {
  const analyticsItems = commerceAnalyticsItems(items);
  return {
    currency: "USD",
    value: analyticsItems.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 1), 0),
    item_count: analyticsItems.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0),
    items: analyticsItems,
    service_types: [...new Set(analyticsItems.map((item) => item.item_category))].join(","),
  };
}

function attributionParams() {
  if (typeof window === "undefined") return {};
  const query = new URLSearchParams(window.location.search || "");
  return Object.fromEntries([["utm_source", query.get("utm_source")], ["utm_medium", query.get("utm_medium")],
    ["utm_campaign", query.get("utm_campaign")], ["utm_content", query.get("utm_content")]]
    .filter(([, value]) => value).map(([key, value]) => [key, safeText(value, 100)]));
}

export function trackBusinessEvent(eventName, params = {}) {
  const definition = ANALYTICS_EVENT_BY_NAME[eventName];
  if (!definition || !["recording", "mixing", "beats", "merch"].includes(definition.domain)) return false;
  const allowed = new Set(definition.parameters); const safe = {};
  for (const [key, value] of Object.entries({...attributionParams(), ...params,
    environment: process.env.REACT_APP_DEPLOY_ENV || process.env.NODE_ENV})) {
    if (!allowed.has(key) || value === undefined || value === null || value === "") continue;
    safe[key] = typeof value === "number" ? safeNumber(value) : safeText(value, 150);
  }
  trackCommerceEvent(eventName, safe); return true;
}

export function trackBeatPlay(track, source = "site_player") {
  trackCommerceEvent("beat_play", beatAnalyticsParams(track, { source: safeText(source, 50) }));
}

export function trackBeatThirtySeconds(track, source = "site_player") {
  trackCommerceEvent("beat_play_30_seconds", beatAnalyticsParams(track, { source: safeText(source, 50) }));
}

export function trackLicenseEvent(eventName, beat, license, price) {
  trackCommerceEvent(eventName, beatAnalyticsParams(beat, {
    license_type: license === "exclusive" ? "exclusive" : "lease",
    currency: "USD",
    value: safeNumber(price),
  }));
}

export function trackSearchEvent({queryLength, resultCount, source = "catalog"} = {}) {
  trackCommerceEvent("search", {
    search_term_length: Math.max(0, Math.min(500, Number(queryLength) || 0)),
    result_count: Math.max(0, Number(resultCount) || 0),
    source: safeText(source, 50),
  });
}

export function trackCheckoutError(error, {stage = "payment", paymentMethod = "card"} = {}) {
  trackCommerceEvent("checkout_error", {
    checkout_stage: safeText(stage, 50),
    payment_method: safeText(paymentMethod, 30),
    error_code: safeText(error?.code || error?.name || "payment_error", 80),
  });
}

export function trackAgreementCompleted(type) {
  const normalized = safeText(type, 30).toLowerCase();
  if (!["lease", "exclusive", "mix", "session"].includes(normalized)) return;
  trackCommerceEvent("agreement_completed", {agreement_type: normalized});
}

function opaqueTransactionId(value) {
  const input = String(value || "");
  let first = 2166136261;
  let second = 2246822519;
  for (let index = 0; index < input.length; index += 1) {
    const code = input.charCodeAt(index);
    first = Math.imul(first ^ code, 16777619);
    second = Math.imul(second ^ code, 3266489917);
  }
  return `purchase_${(first >>> 0).toString(16).padStart(8, "0")}${(second >>> 0).toString(16).padStart(8, "0")}`;
}

export function trackPurchaseOnce(transactionId, params = {}) {
  const rawId = String(transactionId || "").trim();
  if (!rawId) return false;
  const opaqueId = opaqueTransactionId(rawId);
  let ids = [];
  try {
    const parsed = JSON.parse(localStorage.getItem(PURCHASE_DEDUPE_KEY) || "{}");
    ids = parsed?.version === 1 && Array.isArray(parsed.ids)
      ? parsed.ids.filter((id) => typeof id === "string").slice(-99)
      : [];
    if (ids.includes(opaqueId)) return false;
    localStorage.setItem(PURCHASE_DEDUPE_KEY, JSON.stringify({version: 1, ids: [...ids, opaqueId]}));
  } catch {
    // The in-memory caller guard still prevents duplicates during this mount.
  }
  trackCommerceEvent("purchase", {...params, transaction_id: opaqueId});
  return true;
}

export function trackMerchPurchaseOnce(transactionId, params = {}) {
  const rawId = String(transactionId || "").trim();
  if (!rawId) return false;
  const opaqueId = opaqueTransactionId(rawId); let ids = [];
  try {
    const parsed = JSON.parse(localStorage.getItem(MERCH_PURCHASE_DEDUPE_KEY) || "{}");
    ids = parsed?.version === 1 && Array.isArray(parsed.ids) ? parsed.ids.slice(-99) : [];
    if (ids.includes(opaqueId)) return false;
    localStorage.setItem(MERCH_PURCHASE_DEDUPE_KEY, JSON.stringify({version: 1, ids: [...ids, opaqueId]}));
  } catch {}
  return trackBusinessEvent("merch_purchase", {...params, transaction_id: opaqueId, category: "merch"});
}
